import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, Search, Users, Wallet, Download } from "lucide-react";
import { fetchOverview, fetchVendorCredits, fetchTransactions, requestVendorCredits, fetchCreditRequests, cancelSubscription } from "../api";
import {
  formatBillingDate,
  getSubscriptionDatePresentation,
  normalizeSubscription,
  SUBSCRIPTION_LIFECYCLES,
} from "../utils/subscriptionStatus";

const CircularProgress = ({
  value,
  max,
  strokeWidth = 10,
  trackClass = "text-white/20",
  progressClass = "text-white",
  textClass = "text-white",
  className = "w-[60px] h-[60px] sm:w-[80px] sm:h-[80px]"
}) => {
  const size = 100;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safeMax = max > 0 ? max : 1;
  const strokeDashoffset = circumference - (value / safeMax) * circumference;

  return (
    <div className={`relative flex items-center justify-center ${className}`}>
      <svg className="transform -rotate-90 w-full h-full" viewBox={`0 0 ${size} ${size}`}>
        <circle
          className={trackClass}
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className={`${progressClass} transition-all duration-500 ease-in-out`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`font-bold ${textClass}`}>{value}/{max}</span>
      </div>
    </div>
  );
};

const VendorCard = ({ vendor }) => {
  const [showRequest, setShowRequest] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [requestReason, setRequestReason] = useState("");
  const [requestAmount, setRequestAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const hasPending = (vendor.pendingRequests && vendor.pendingRequests.length > 0) || isPending;

  const toggleTable = () => {
    setShowTable(!showTable);
  };

  const toggleRequest = (e) => {
    e.stopPropagation();
    if (hasPending) return;
    setShowRequest(!showRequest);
  };

  const handleSubmitRequest = async () => {
    if (!requestReason) {
      alert("Please provide a reason");
      return;
    }
    setSubmitting(true);
    try {
      await requestVendorCredits(vendor.vendor_id, undefined, requestReason);
      alert("Request submitted successfully!");
      setShowRequest(false);
      setRequestReason("");
      setRequestAmount("");
      setIsPending(true);
    } catch (error) {
      console.error(error);
      alert("Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-[20px] p-4 sm:p-5 flex flex-col bg-white">
      <div
        className="flex items-center justify-between gap-2 pb-3 sm:pb-4 border-b border-gray-100 cursor-pointer min-w-0"
        onClick={toggleTable}
      >
        <div className="flex items-center gap-1.5 sm:gap-2 text-[#0ea5e9] min-w-0 flex-1">
          {showTable ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
          <span className="font-bold text-[13px] sm:text-[15px] truncate">{vendor.name}</span>
        </div>
        <span className="text-[10px] sm:text-[12px] text-gray-400 font-medium shrink-0 text-right leading-tight pl-2 max-w-[50%]">
          Associated since: {vendor.date}
        </span>
      </div>

      <div className="flex flex-row items-start justify-between gap-2 sm:gap-3 px-1 sm:px-2 pt-4 sm:pt-5 pb-1 sm:pb-2 min-w-0">
        <div className="flex flex-col items-start ml-0 sm:ml-3 shrink-0">
          <span className="text-[10px] sm:text-[12px] text-gray-400 font-medium mb-2 sm:mb-2.5 leading-tight">
            Used / Total
          </span>
          <CircularProgress
            value={vendor.used}
            max={vendor.total}
            strokeWidth={10}
            trackClass="text-gray-100"
            progressClass="text-[#8b22e2]"
            textClass="text-gray-800 text-[9px] sm:text-[11px]"
            className="w-[56px] h-[56px] sm:w-[70px] sm:h-[70px]"
          />
        </div>
        <div className="flex flex-col items-end gap-2 min-w-0 sm:min-w-[140px] shrink-0">
          <button
            onClick={toggleRequest}
            disabled={hasPending}
            className={`${hasPending ? 'bg-[#94a3b8] cursor-not-allowed' : 'bg-[#8b22e2] hover:bg-[#7a1bc7]'} text-white text-[11px] sm:text-[13px] font-bold px-4 sm:px-6 py-2 sm:py-2.5 rounded-full shadow-sm transition-colors whitespace-nowrap`}
          >
            {hasPending ? 'Request Pending' : 'Request Credits'}
          </button>
          {hasPending && vendor.pendingRequests?.[0]?.dateStr && (
            <p className="text-[10px] sm:text-[12px] text-gray-400 font-medium text-right leading-snug max-w-full">
              Credits requested: {vendor.pendingRequests[0].dateStr}
            </p>
          )}
        </div>
      </div>

      {showRequest && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="mb-3">
            <textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              className="w-full border border-gray-200 rounded-xl p-3 sm:p-4 text-[13px] sm:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#8b22e2]/30 focus:border-[#8b22e2]/30 placeholder-gray-500 resize-none h-24 shadow-sm"
              placeholder="Enter reason for credit request..."
            ></textarea>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <button
              onClick={() => setShowRequest(false)}
              disabled={submitting}
              className="flex-1 bg-[#e2e8f0] text-gray-700 text-xs sm:text-[13px] font-bold py-2.5 sm:py-3 rounded-xl hover:bg-[#cbd5e1] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitRequest}
              disabled={submitting}
              className="flex-1 bg-[#8b22e2] text-white text-xs sm:text-[13px] font-bold py-2.5 sm:py-3 rounded-xl hover:bg-[#7a1bc7] transition-colors shadow-sm disabled:opacity-70"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      )}

      {showTable && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          {/* {vendor.pendingRequests && vendor.pendingRequests.length > 0 && (
            <div className="flex flex-col gap-2 mb-4">
              {vendor.pendingRequests.map((req, idx) => (
                <div key={req.request_id || idx} className="bg-[#fef9c3] border border-[#eab308] text-[#ea580c] px-4 py-3 rounded-xl text-[12px] sm:text-[13px] flex flex-col gap-1.5">
                  <div>
                    <span className="font-bold">Pending</span> - Credits Requested on {req.dateStr}
                  </div>
                </div>
              ))}
            </div>
          )} */}
          <div className="grid grid-cols-4 gap-1 sm:gap-2 bg-[#cbd5e1]/50 rounded-xl p-2.5 px-3 mb-3">
            <span className="text-[10px] sm:text-xs font-bold text-[#0ea5e9] text-center">Credit Dt.</span>
            <span className="text-[10px] sm:text-xs font-bold text-[#0ea5e9] text-center">Credits</span>
            <span className="text-[10px] sm:text-xs font-bold text-[#0ea5e9] text-center">Used</span>
            <span className="text-[10px] sm:text-xs font-bold text-[#0ea5e9] text-center">Expiry Dt.</span>
          </div>
          <div className="flex flex-col gap-2.5">
            {vendor.items?.map((item, idx) => (
              <div key={idx} className="grid grid-cols-4 gap-1 sm:gap-2 bg-gray-50/50 border border-gray-100 rounded-xl p-3 shadow-sm">
                <span className="text-[10px] sm:text-[13px] font-medium text-gray-800 text-center">{item.date}</span>
                <span className="text-[10px] sm:text-[13px] font-medium text-gray-800 text-center">{item.total}</span>
                <span className="text-[10px] sm:text-[13px] font-medium text-gray-800 text-center">{item.used}</span>
                <span className="text-[10px] sm:text-[13px] font-medium text-gray-800 text-center">{item.expiry}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Overview() {
  const navigate = useNavigate();
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [inlineHistory, setInlineHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [canceling, setCanceling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const loadData = (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    Promise.all([fetchOverview(), fetchVendorCredits(), fetchTransactions(), fetchCreditRequests()])
      .then(([overviewRes, creditsRes, transRes, reqsRes]) => {
        setData(overviewRes.data);

        const pendingRequests = (reqsRes.data?.requests || []).filter(r => r.status === "PENDING");

        const grouped = (creditsRes.data?.vendor_credits || []).reduce((acc, v) => {
          const vid = v.vendor_id;
          if (!acc[vid]) {
            acc[vid] = {
              vendor_id: vid,
              name: v.vendor_name,
              total: 0,
              used: 0,
              items: [],
              earliest_created: v.created_at,
              pendingRequests: []
            };
          }
          const total = parseInt(v.total_credits, 10) || 0;
          const used = parseInt(v.used_credits, 10) || 0;
          acc[vid].total += total;
          acc[vid].used += used;
          acc[vid].items.push({
             total,
             used,
             date: v.created_at ? new Date(parseInt(v.created_at, 10)).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' }) : 'N/A',
             expiry: v.expiry_date ? new Date(v.expiry_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' }) : 'N/A'
          });
          if (v.created_at && (!acc[vid].earliest_created || v.created_at < acc[vid].earliest_created)) {
            acc[vid].earliest_created = v.created_at;
          }
          return acc;
        }, {});

        const mappedVendors = Object.values(grouped).map(gv => {
          const vendorPendingRequests = pendingRequests.filter(r => r.vendor_id === gv.vendor_id);
          gv.pendingRequests = vendorPendingRequests.sort((a, b) => b.created_at - a.created_at).map(r => {
             const d = new Date(parseInt(r.created_at, 10));
             const day = String(d.getDate()).padStart(2, '0');
             const month = d.toLocaleString('en-US', { month: 'short' });
             const year = String(d.getFullYear()).slice(-2);
             return { dateStr: `${day} ${month} ${year}`, amount: r.amount, reason: r.reason, request_id: r.request_id };
          });
          gv.date = gv.earliest_created ? new Date(parseInt(gv.earliest_created, 10)).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' }) : 'N/A';
          return gv;
        });
        setVendors(mappedVendors);

        const rawList = 
          transRes.data?.transactions || 
          transRes.data?.history || 
          transRes.history || 
          transRes.transactions || 
          (Array.isArray(transRes.data) ? transRes.data : []) || 
          (Array.isArray(transRes) ? transRes : []);
        const mappedTrans = rawList.filter(Boolean).map(t => {
          const isCreditHistory = !!t.usage_id || !!t.amount_consumed || !!t.tryon_category;
          
          let plan = '';
          let billingCycle = '';
          let date = 'N/A';
          let planExpiryDate = 'N/A';
          let invoice = 'N/A';
          let status = 'PAID';
          let amount = 'N/A';

          // Format Date
          const rawDate = t.created_at || t.createdAt || t.created;
          let dateObj = null;
          if (rawDate) {
            if (typeof rawDate === 'number' || /^\d+$/.test(rawDate)) {
              const numDate = parseInt(rawDate, 10);
              if (numDate < 9999999999) {
                dateObj = new Date(numDate * 1000);
              } else {
                dateObj = new Date(numDate);
              }
            } else {
              dateObj = new Date(rawDate);
            }
          }
          if (dateObj && !isNaN(dateObj.getTime())) {
            date = dateObj.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' });
          }

          if (isCreditHistory) {
            // Mapping for credit history logs
            const category = t.tryon_category ? t.tryon_category.charAt(0).toUpperCase() + t.tryon_category.slice(1) : 'TryOn';
            plan = `TryOn (${category})`;
            billingCycle = t.credit_source || 'Personal';
            
            invoice = t.usage_id || 'N/A';
            status = t.outcome || 'SUCCESS';
            
            const consumed = typeof t.amount_consumed === 'number' ? t.amount_consumed : parseInt(t.amount_consumed, 10) || 0;
            amount = status.toUpperCase() === 'REFUNDED' ? `+${consumed} TryOn` : `-${consumed} TryOn`;
            planExpiryDate = t.refund_reason || 'N/A';
          } else {
            // Traditional payment transaction mapping
            let desc = t.plan_name || t.description;
            if (!desc && t.plan_id) {
              const pid = t.plan_id.replace('plan_', '');
              desc = pid.charAt(0).toUpperCase() + pid.slice(1) + " Plan";
            }
            if (!desc) desc = 'Starter Plan';

            const titleMatch = desc.match(/([a-zA-Z0-9\s\-]+)\sPlan/i) || desc.match(/([a-zA-Z0-9\s\-]+)/i);
            plan = titleMatch ? titleMatch[1] : desc;
            
            billingCycle = desc.toLowerCase().includes('yearly') ? 'Yearly' : desc.toLowerCase().includes('one-time') ? 'One-time' : 'Monthly';
            
            if (dateObj && !isNaN(dateObj.getTime())) {
              const planExpiryObj = new Date(dateObj);
              if (billingCycle === 'Yearly') {
                planExpiryObj.setFullYear(planExpiryObj.getFullYear() + 1);
              } else if (billingCycle === 'One-time') {
                planExpiryObj.setDate(planExpiryObj.getDate() + 2); // e.g. 2-Day Pass
              } else {
                planExpiryObj.setMonth(planExpiryObj.getMonth() + 1);
              }
              planExpiryDate = planExpiryObj.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: '2-digit' });
            }

            invoice = t.transaction_id || t.invoice_id || t.invoiceId || t.invoice || 'N/A';
            
            const currencySymbol = (t.currency || '').toUpperCase() === 'INR' ? '₹' : (t.currency || '').toUpperCase();
            let parsedAmount = typeof t.amount === 'number' ? t.amount : parseFloat(t.amount) || 0;
            let finalAmount = parsedAmount;
            if (parsedAmount > 100 && Number.isInteger(parsedAmount)) {
              finalAmount = parsedAmount / 100;
            }
            amount = `${currencySymbol}${finalAmount.toLocaleString()}`;
            status = t.status || 'PAID';
          }

          return {
            plan,
            date,
            amount,
            billingCycle,
            planExpiryDate,
            invoice,
            status
          };
        });
        setInlineHistory(mappedTrans.slice(0, 5)); // show only top 5

        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError(err.message || 'Failed to load overview data.');
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const confirmCancelSubscription = async () => {
    setShowCancelModal(false);
    setCanceling(true);
    setMessage(null);
    try {
      const res = await cancelSubscription();
      if (res?.status === 'success') {
        setMessage({ type: 'success', text: res.message || "Subscription cancellation scheduled successfully!" });
        loadData(false); // Reload data silently
      } else {
        throw new Error(res?.message || 'Cancellation failed');
      }
    } catch (err) {
      console.error("Failed to cancel subscription:", err);
      setMessage({ type: 'error', text: `Cancellation failed: ${err.message || 'Please try again.'}` });
    } finally {
      setCanceling(false);
    }
  };

  const downloadInvoice = (transaction) => {
    const invoiceContent = `MIGGUS - INVOICE
======================================
Invoice / Txn ID : ${transaction.invoice || 'N/A'}
Plan Name        : ${transaction.plan || 'N/A'} Plan
Billing Cycle    : ${transaction.billingCycle || 'N/A'}
Transaction Date : ${transaction.date || 'N/A'}
Plan Expiry Date : ${transaction.planExpiryDate || 'N/A'}
Amount Paid      : ${transaction.amount || 'N/A'}
Payment Status   : ${(transaction.status || 'paid').toUpperCase()}

======================================
Thank you for your business with Miggus!
If you have any questions, contact billing@miggus.com.`;

    const blob = new Blob([invoiceContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Invoice-${transaction.invoice || 'Transaction'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredVendors = useMemo(() => {
    return vendors.filter(v => v.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [vendors, searchQuery]);

  if (loading) {
    return <div className="flex-1 flex items-center justify-center"><div className="text-[#9325e6] animate-pulse font-bold">Loading...</div></div>;
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <div className="bg-red-50 text-red-600 p-6 rounded-2xl max-w-lg shadow-sm border border-red-100">
          <h3 className="font-bold text-lg mb-2">Error Loading Dashboard</h3>
          <p className="text-sm">{error}</p>
          <p className="text-xs mt-4 text-red-500/80">Check your developer console for more details. Ensure your token is valid and CORS is enabled on the API Gateway.</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { subscription, usage_metrics } = data;
  const normalizedSubscription = normalizeSubscription(subscription);
  const billingDatePresentation = getSubscriptionDatePresentation(normalizedSubscription);
  const currentPlan = normalizedSubscription.planName;
  const billingDate = formatBillingDate(billingDatePresentation.date, { fallback: 'N/A' });
  const priceAmount = normalizedSubscription.priceAmount;
  const currency = normalizedSubscription.currency === 'INR' ? '₹' : normalizedSubscription.currency;
  const billingCycle = normalizedSubscription.frequency === 'annual' ? 'yr' : 'mo';
  const plansButtonLabel = [SUBSCRIPTION_LIFECYCLES.FREE, SUBSCRIPTION_LIFECYCLES.EXPIRED].includes(normalizedSubscription.lifecycle)
    ? 'View Plans'
    : normalizedSubscription.lifecycle === SUBSCRIPTION_LIFECYCLES.PAST_DUE
      ? 'Billing Help'
      : 'Change Plan';

  const creditsUsed = usage_metrics?.used_credits || 0;
  const creditsTotal = usage_metrics?.total_credits || 0;
  const creditsRemaining = usage_metrics?.credits_left || 0;
  const activeVendors = usage_metrics?.active_vendors || 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 h-full overflow-y-auto w-full">
      {message && (
        <div className={`mb-5 p-4 rounded-xl font-semibold w-full text-center text-[14px] ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
          {message.text}
        </div>
      )}
      {normalizedSubscription.lifecycle === SUBSCRIPTION_LIFECYCLES.CANCELLATION_SCHEDULED && (
        <div className="mb-5 w-full rounded-xl border border-amber-200 bg-amber-50 p-4 text-center text-[14px] font-semibold text-amber-800">
          Cancellation scheduled. Your plan and credits remain available until {formatBillingDate(normalizedSubscription.cancellationEffectiveDate || normalizedSubscription.nextBillingDate)}.
        </div>
      )}
      {normalizedSubscription.lifecycle === SUBSCRIPTION_LIFECYCLES.PAST_DUE && (
        <div className="mb-5 w-full rounded-xl border border-red-200 bg-red-50 p-4 text-center text-[14px] font-semibold text-red-700">
          Your subscription payment is past due. Please resolve the billing issue or contact support before changing plans.
        </div>
      )}
      {normalizedSubscription.lifecycle === SUBSCRIPTION_LIFECYCLES.EXPIRED && (
        <div className="mb-5 w-full rounded-xl border border-slate-200 bg-white p-4 text-center text-[14px] font-semibold text-slate-700">
          Your previous subscription has expired. Free-tier access is currently active.
        </div>
      )}
      {normalizedSubscription.hasActivePass && (
        <div className="mb-5 w-full rounded-xl border border-purple-200 bg-purple-50 p-4 text-center text-[14px] font-semibold text-purple-700">
          Your 2-Day Pass is active until {formatBillingDate(normalizedSubscription.passExpiryDate)}.
        </div>
      )}
      {/* Top Purple Card — compact row on small screens; three columns on lg (donut | Payment History | Change Plan) */}
      <div className="bg-[#9333EA] rounded-[22px] sm:rounded-[28px] p-4 sm:p-8 mb-5 sm:mb-8 text-white shadow-lg">
        {/* Small / medium: stacked header + donut + buttons row */}
        <div className="lg:hidden">
          <div className="grid grid-cols-3 gap-2 sm:gap-8 mb-6 sm:mb-8">
            <div className="flex flex-col items-start min-w-0 pr-1">
              <span className="text-[9px] sm:text-[11px] font-bold tracking-[0.12em] uppercase text-white mb-1.5 sm:mb-2 leading-tight">
                Current Plan
              </span>
              <span className="text-[17px] sm:text-[30px] font-bold leading-tight tracking-tight truncate w-full">
                {currentPlan}
              </span>
            </div>
            <div className="flex flex-col items-center text-center min-w-0 px-0.5">
              <span className="text-[9px] sm:text-[11px] font-bold tracking-[0.12em] uppercase text-white mb-1.5 sm:mb-2 leading-tight">
                {billingDatePresentation.label}
              </span>
              <span className="text-[14px] sm:text-[22px] font-bold leading-tight whitespace-nowrap">
                {billingDate}
              </span>
            </div>
            <div className="flex flex-col items-end text-right min-w-0 pl-1">
              <span className="text-[9px] sm:text-[11px] font-bold tracking-[0.12em] uppercase text-white mb-1.5 sm:mb-2 leading-tight">
                Price
              </span>
              <span className="text-[14px] sm:text-[22px] font-bold leading-tight whitespace-nowrap">
                {currency}
                {priceAmount.toLocaleString()}
                <span className="font-bold">/{billingCycle}</span>
              </span>
            </div>
          </div>
          <div className="flex flex-row items-center justify-between gap-3 sm:gap-6">
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <CircularProgress
                value={creditsUsed}
                max={creditsTotal}
                strokeWidth={11}
                trackClass="text-white/25"
                progressClass="text-white"
                textClass="text-white text-[11px] sm:text-[13px] font-bold"
                className="w-[76px] h-[76px] sm:w-[96px] sm:h-[96px]"
              />
              <span className="text-[10px] sm:text-[12px] font-bold text-white tracking-tight">
                TryOns Used
              </span>
            </div>
            <div className="flex flex-row flex-wrap items-center justify-end gap-2 sm:gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setShowPaymentHistory(!showPaymentHistory)}
                className="inline-flex items-center justify-center gap-1 bg-white text-[#9333EA] hover:bg-white/95 active:bg-gray-100 text-[11px] sm:text-[14px] font-bold px-3 sm:px-5 py-2 sm:py-2.5 rounded-full shadow-sm transition-colors whitespace-nowrap"
              >
                {showPaymentHistory ? (
                  <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                )}
                <span>Payment History</span>
              </button>
              <button
                type="button"
                onClick={() => navigate('/plans')}
                className="inline-flex items-center justify-center bg-white text-[#9333EA] hover:bg-white/95 active:bg-gray-100 text-[11px] sm:text-[14px] font-bold px-3 sm:px-5 py-2 sm:py-2.5 rounded-full shadow-sm transition-colors whitespace-nowrap"
              >
                {plansButtonLabel}
              </button>
              {normalizedSubscription.canCancel && (
                <button
                  type="button"
                  disabled={canceling}
                  onClick={() => setShowCancelModal(true)}
                  className="inline-flex items-center justify-center bg-red-100 hover:bg-red-200 active:bg-red-300 text-red-700 text-[11px] sm:text-[14px] font-bold px-3 sm:px-5 py-2 sm:py-2.5 rounded-full shadow-sm transition-colors whitespace-nowrap disabled:opacity-50"
                >
                  {canceling ? 'Canceling...' : 'Cancel Plan'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Large screens: 3 columns × 4 rows — row 3 vertically centers donut with each pill button */}
        <div className="hidden lg:grid lg:grid-cols-3 lg:gap-x-10 lg:gap-y-3">
          <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-white/85">
            Current Plan
          </span>
          <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-white/85 text-center">
            {billingDatePresentation.label}
          </span>
          <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-white/85 text-right">
            Price
          </span>

          <span className="text-[32px] xl:text-[36px] font-bold leading-tight tracking-tight min-w-0 truncate">
            {currentPlan}
          </span>
          <span className="text-[22px] xl:text-[24px] font-bold leading-tight whitespace-nowrap text-center">
            {billingDate}
          </span>
          <span className="text-[22px] xl:text-[24px] font-bold leading-tight whitespace-nowrap text-right">
            {currency}
            {priceAmount.toLocaleString()}
            <span className="font-bold">/{billingCycle}</span>
          </span>

          <div className="flex min-h-[112px] xl:min-h-[120px] items-center justify-start py-1">
            <div className="flex flex-col items-center gap-1.5">
              <CircularProgress
                value={creditsUsed}
                max={creditsTotal}
                strokeWidth={11}
                trackClass="text-white/25"
                progressClass="text-white"
                textClass="text-white text-[13px] font-bold"
                className="w-[96px] h-[96px] xl:w-[104px] xl:h-[104px]"
              />
              <span className="text-[12px] font-bold text-white tracking-tight">
                TryOns Used
              </span>
            </div>
          </div>
          <div className="flex min-h-[112px] xl:min-h-[120px] items-center justify-center py-1">
            <button
              type="button"
              onClick={() => setShowPaymentHistory(!showPaymentHistory)}
              className="inline-flex items-center justify-center gap-1.5 bg-white text-[#9333EA] hover:bg-white/95 active:bg-gray-100 text-[14px] font-bold px-6 py-2.5 rounded-full shadow-sm transition-colors whitespace-nowrap"
            >
              {showPaymentHistory ? (
                <ChevronUp className="w-4 h-4 shrink-0" />
              ) : (
                <ChevronDown className="w-4 h-4 shrink-0" />
              )}
              <span>Payment History</span>
            </button>
          </div>
          <div className="flex min-h-[112px] xl:min-h-[120px] flex-col gap-2.5 items-end justify-center py-1">
            <button
              type="button"
              onClick={() => navigate('/plans')}
              className="inline-flex items-center justify-center bg-white text-[#9333EA] hover:bg-white/95 active:bg-gray-100 text-[14px] font-bold px-6 py-2.5 rounded-full shadow-sm transition-colors whitespace-nowrap w-full max-w-[180px]"
            >
              {plansButtonLabel}
            </button>
            {normalizedSubscription.canCancel && (
              <button
                type="button"
                disabled={canceling}
                onClick={() => setShowCancelModal(true)}
                className="inline-flex items-center justify-center bg-red-100/35 text-red-100 hover:bg-red-200 hover:text-red-700 active:bg-red-300 text-[13px] font-bold px-6 py-1.5 rounded-full border border-red-200/20 transition-colors whitespace-nowrap w-full max-w-[180px] disabled:opacity-50"
              >
                {canceling ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Inline Payment History Table */}
      {showPaymentHistory && (
        <div className="bg-white rounded-[16px] sm:rounded-3xl shadow-sm mb-5 sm:mb-8 overflow-hidden">
          <div className="grid grid-cols-3 bg-[#dce4eb] px-4 py-3.5 sm:p-5">
            <span className="text-[13px] sm:text-[15px] font-bold text-[#0ea5e9]">Plan</span>
            <span className="text-[13px] sm:text-[15px] font-bold text-[#0ea5e9]">Date</span>
            <span className="text-[13px] sm:text-[15px] font-bold text-[#0ea5e9]">Amount</span>
          </div>
          <div className="flex flex-col">
            {inlineHistory.map((row, idx) => (
              <div
                key={idx}
                className={`grid grid-cols-3 px-4 py-3.5 sm:p-5 items-center ${idx % 2 !== 0 ? 'bg-gray-50/80' : 'bg-white'}`}
              >
                <span className="text-[13px] sm:text-[15px] font-medium text-[#1a1e2e]">{row.plan}</span>
                <span className="text-[13px] sm:text-[15px] font-medium text-slate-500">{row.date}</span>
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="text-[13px] sm:text-[15px] font-medium text-[#1a1e2e]">{row.amount}</span>
                  <Download className="w-[14px] h-[14px] sm:w-4 sm:h-4 text-[#8b22e2] cursor-pointer" onClick={() => downloadInvoice(row)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two Small Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-6 mb-5 sm:mb-8">
        <div className="bg-white rounded-[20px] sm:rounded-3xl p-3.5 sm:p-6 shadow-sm flex flex-col justify-between min-h-[90px] sm:min-h-[140px]">
          <h3 className="text-[11px] sm:text-[15px] font-bold text-[#374151] mb-3 sm:mb-6 leading-tight">Vendors with credits</h3>
          <div className="flex items-center flex-1">
            <div className="bg-[#eef8ff] text-[#3b82f6] p-2.5 sm:p-4 rounded-xl sm:rounded-2xl shrink-0">
              <Users className="w-4 h-4 sm:w-7 sm:h-7" />
            </div>
            <div className="flex-1 flex justify-center">
              <span className="text-[16px] sm:text-3xl font-extrabold text-[#111827] tracking-tight">{activeVendors}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-[20px] sm:rounded-3xl p-3.5 sm:p-6 shadow-sm flex flex-col justify-between min-h-[90px] sm:min-h-[140px]">
          <h3 className="text-[11px] sm:text-[15px] font-bold text-[#374151] mb-3 sm:mb-6 leading-tight">Personal Credits - Used/Total</h3>
          <div className="flex items-center flex-1">
            <div className="bg-[#ecfdf5] text-[#10b981] p-2.5 sm:p-4 rounded-xl sm:rounded-2xl shrink-0">
              <Wallet className="w-4 h-4 sm:w-7 sm:h-7" />
            </div>
            <div className="flex-1 flex justify-center">
              <span className="text-[16px] sm:text-3xl font-extrabold text-[#111827] tracking-tight">{creditsUsed.toLocaleString()} / {creditsTotal.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Vendor Credits Summary Section */}
      <div className="bg-white rounded-[24px] sm:rounded-3xl p-5 sm:p-8 shadow-sm mb-8 sm:mb-10">
        <h3 className="text-[14px] sm:text-[15px] font-bold text-gray-800 mb-4 sm:mb-6">Vendor Credits Summary</h3>

        <div className="relative mb-5 sm:mb-6">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-4 w-4 text-gray-400" />
          </div>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-white border border-gray-200 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-[#8b22e2]/20 focus:border-[#8b22e2]/30 transition-all placeholder-gray-400 shadow-sm"
            placeholder="Search by vendors..."
          />
        </div>

        <div className="flex flex-col gap-3 sm:gap-4">
          {filteredVendors.length === 0 ? (
            <div className="text-center text-gray-400 py-4 text-sm">No vendors found.</div>
          ) : (
            filteredVendors.map((vendor, index) => (
              <VendorCard key={index} vendor={vendor} />
            ))
          )}
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="text-[18px] font-bold text-gray-900 mb-2">Cancel Subscription</h3>
            <p className="text-[13px] text-gray-500 mb-6">
              Are you sure you want to cancel your subscription? Your plan and credits will remain active until {formatBillingDate(normalizedSubscription.nextBillingDate, { fallback: 'the end of the current billing period' })}.
            </p>
            <div className="flex items-center gap-3 w-full">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors text-[13px]"
              >
                Keep Plan
              </button>
              <button
                onClick={confirmCancelSubscription}
                className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors text-[13px]"
              >
                Cancel Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
